import { useState } from 'react'
import { BookmarkPlus, Eye, EyeOff, Pencil, Plus, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import { useShallow } from 'zustand/react/shallow'
import useStore, {
  PLAN_SLOTS,
  PLAN_SLOT_NAMES,
  visiblePlanKeys,
} from '@/stores/useStore'
import { planDisplayName } from '@/lib/planDisplayNames'

/**
 * The header's plan ledger is the single management surface for comparison
 * plans. Visibility is the comparison state; there is no selected/active plan
 * while multiple designs are shown. Saved-design actions name their target
 * explicitly instead of borrowing a workspace-wide focus pointer.
 */
export default function PlanChips({ manage = false, savedPlansOnly = false, showTemplateHeading = true, addLabel = 'Add comparison option', className = '' }) {
  const setPlanVisible = useStore((state) => state.setPlanVisible)
  const addPlan = useStore((state) => state.addPlan)
  const removePlan = useStore((state) => state.removePlan)
  const renamePlan = useStore((state) => state.renamePlan)
  const planTemplates = useStore((state) => state.planTemplates)
  const savePlanTemplate = useStore((state) => state.savePlanTemplate)
  const applyPlanTemplate = useStore((state) => state.applyPlanTemplate)
  const removePlanTemplate = useStore((state) => state.removePlanTemplate)
  const names = useStore(useShallow((state) => PLAN_SLOTS
    .map((key) => (state[key] ? (state[key].name || PLAN_SLOT_NAMES[key]) : ''))))
  const hiddenKeys = useStore((state) => state.hiddenPlanKeys.join('|')).split('|').filter(Boolean)
  const visibleKeys = useStore((state) => visiblePlanKeys(state).join('|')).split('|').filter(Boolean)
  const [editingKey, setEditingKey] = useState(null)
  const [nameDraft, setNameDraft] = useState('')
  const [savingTemplate, setSavingTemplate] = useState(false)
  const [templateNameDraft, setTemplateNameDraft] = useState('')
  const [designSourceKey, setDesignSourceKey] = useState('planA')

  const entries = PLAN_SLOTS
    .map((key, index) => ({ key, storedName: names[index], name: planDisplayName(key, names[index]) }))
    .filter((entry) => entry.storedName)
  const canAdd = entries.length < PLAN_SLOTS.length
  const sourceEntry = entries.find((entry) => entry.key === designSourceKey) || entries[0]
  const sourceKey = sourceEntry?.key || 'planA'
  const sourceName = sourceEntry?.name || PLAN_SLOT_NAMES[sourceKey]

  if (entries.length < 2 && !manage) return null

  const handleAdd = () => {
    const slot = addPlan('planA')
    if (!slot) return
    toast.success(`${planDisplayName(slot, PLAN_SLOT_NAMES[slot])} added`, {
      description: 'Copied from Current Plan and added to the comparison.',
    })
  }

  const handleRemove = (entry) => {
    removePlan(entry.key)
    toast.success(`${entry.name} deleted`, {
      description: 'Use Undo in the header to restore it.',
    })
  }

  const startRename = (entry) => {
    setEditingKey(entry.key)
    setNameDraft(entry.name)
  }

  const finishRename = (entry) => {
    const nextName = nameDraft.trim().replace(/\s+/g, ' ').slice(0, 40)
    if (nextName && nextName !== entry.name) renamePlan(entry.key, nextName)
    setEditingKey(null)
  }

  const startSaveTemplate = () => {
    setTemplateNameDraft(sourceEntry?.storedName === PLAN_SLOT_NAMES[sourceKey] ? '' : sourceName)
    setSavingTemplate(true)
  }

  const finishSaveTemplate = () => {
    const result = savePlanTemplate(sourceKey, templateNameDraft)
    if (!result) return
    const templateName = templateNameDraft.trim()
    setSavingTemplate(false)
    setTemplateNameDraft('')
    toast.success(result.updated ? `Updated “${templateName}”` : `Saved “${templateName}”`)
  }

  const handleApplyTemplate = (template, destinationKey) => {
    const destination = entries.find((entry) => entry.key === destinationKey)
    if (!destination || !applyPlanTemplate(template.id, destinationKey)) return
    toast.success(`Applied “${template.name}” to ${destination.name}`)
  }

  const handleRemoveTemplate = (template) => {
    if (!removePlanTemplate(template.id)) return
    toast.success(`Deleted “${template.name}”`)
  }

  if (!manage) {
    return (
      <div role="list" aria-label="Visible comparison options" className={`flex flex-wrap items-center gap-1 ${className}`}>
        {entries.filter((entry) => visibleKeys.includes(entry.key)).map((entry) => (
          <span key={entry.key} role="listitem" className={entry.key === 'planA' ? 'rounded-full border border-primary/30 bg-primary/5 px-2.5 py-1 text-xs font-medium text-foreground' : 'rounded-full border border-border bg-background px-2.5 py-1 text-xs font-medium text-muted-foreground'}>
            {entry.name}{entry.key === 'planA' && <span className="ml-1 text-[9px] uppercase tracking-wide text-primary">Starting point</span>}
          </span>
        ))}
      </div>
    )
  }

  return (
    <div className={`space-y-3 ${className}`}>
      {!savedPlansOnly ? <>
      <div role="list" aria-label="Comparison options" className="overflow-hidden rounded-md border border-border bg-background">
        {entries.map((entry) => {
          const visible = !hiddenKeys.includes(entry.key)
          const baseline = entry.key === 'planA'
          const canHide = !baseline && visible && visibleKeys.length > 1

          return (
            <div
              key={entry.key}
              role="listitem"
              className={`flex min-h-11 items-center gap-2 border-b border-border px-2.5 py-1.5 last:border-b-0 ${visible ? '' : 'bg-muted/20 text-muted-foreground'}`}
            >
              {editingKey === entry.key ? (
                <div className="min-w-0 flex-1">
                  <input
                    autoFocus
                    name={`plan-name-${entry.key}`}
                    autoComplete="off"
                    value={nameDraft}
                    maxLength={40}
                    aria-label={`Rename ${entry.name}`}
                    onChange={(event) => setNameDraft(event.target.value)}
                    onBlur={() => finishRename(entry)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') event.currentTarget.blur()
                      if (event.key === 'Escape') {
                        setNameDraft(entry.name)
                        setEditingKey(null)
                      }
                    }}
                    className="h-7 w-full rounded border border-border bg-background px-2 text-xs text-foreground outline-none focus:ring-1 focus:ring-ring"
                  />
                </div>
              ) : (
                <div className="flex min-w-0 flex-1 items-center gap-2">
                  <span className="flex min-w-0 items-baseline gap-2">
                    <span className="truncate text-xs font-semibold text-foreground">{entry.name}</span>
                    {(baseline || !visible) ? (
                      <span className="shrink-0 text-[10px] text-muted-foreground">
                        {baseline ? 'Starting point' : 'Hidden'}
                      </span>
                    ) : null}
                  </span>
                </div>
              )}

              {editingKey !== entry.key ? (
                <button
                  type="button"
                  aria-label={`Rename ${entry.name}`}
                  title={`Rename ${entry.name}`}
                  onClick={() => startRename(entry)}
                  className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </button>
              ) : null}

              <button
                type="button"
                aria-label={visible ? `Hide ${entry.name}` : `Show ${entry.name}`}
                title={baseline ? 'Current Plan is the starting point and stays visible' : visible ? `Hide ${entry.name} from comparisons` : `Show ${entry.name} in comparisons`}
                disabled={baseline || (visible && !canHide)}
                onClick={() => setPlanVisible(entry.key, !visible)}
                className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:cursor-not-allowed disabled:opacity-35"
              >
                {visible ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
              </button>

              {!baseline ? (
                <button
                  type="button"
                  aria-label={`Delete ${entry.name}`}
                  title={`Delete ${entry.name}`}
                  onClick={() => handleRemove(entry)}
                  className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              ) : <span className="h-7 w-7 shrink-0" aria-hidden="true" />}
            </div>
          )
        })}
      </div>

      {canAdd ? (
        <button
          type="button"
          onClick={handleAdd}
          className="flex w-full items-center justify-center gap-1.5 rounded-md border border-dashed border-border bg-background px-3 py-2 text-xs font-medium text-muted-foreground transition-colors hover:border-primary/40 hover:bg-primary/5 hover:text-foreground"
        >
          <Plus className="h-3.5 w-3.5" />
          {addLabel}
        </button>
      ) : null}
      </> : null}

      {savedPlansOnly ? <section aria-labelledby={showTemplateHeading ? 'plan-templates-heading' : undefined} aria-label={showTemplateHeading ? undefined : 'Plan templates'}>
        <div className="mb-2 flex items-start justify-between gap-3">
          {showTemplateHeading ? <div>
            <p id="plan-templates-heading" className="text-sm font-semibold text-foreground">Plan Templates</p>
            <p className="mt-0.5 text-xs leading-5 text-muted-foreground">Save one option and apply it to another client or comparison.</p>
          </div> : <span />}
          {!savingTemplate ? (
            <button
              type="button"
              onClick={startSaveTemplate}
              className="inline-flex h-7 shrink-0 items-center gap-1.5 rounded border border-border bg-background px-2 text-[11px] font-medium text-foreground transition-colors hover:bg-muted"
            >
              <BookmarkPlus className="h-3.5 w-3.5" />
              Save a plan
            </button>
          ) : null}
        </div>

        {planTemplates.length ? (
          <div role="list" aria-label="Plan templates" className="mb-2 max-h-[12rem] overflow-y-auto rounded-md border border-border bg-background">
            {planTemplates.map((template) => (
              <div key={template.id} role="listitem" className="flex min-h-10 items-center gap-2 border-b border-border px-2 last:border-b-0">
                <span className="min-w-0 flex-1 truncate text-xs font-medium text-foreground">{template.name}</span>
                <select
                  value=""
                  aria-label={`Apply ${template.name} to a plan`}
                  onChange={(event) => handleApplyTemplate(template, event.target.value)}
                  className="h-7 max-w-32 rounded border border-border bg-background px-1.5 text-[11px] text-foreground"
                >
                  <option value="" disabled>Apply to…</option>
                  {entries.map((entry) => <option key={entry.key} value={entry.key}>{entry.name}</option>)}
                </select>
                <button
                  type="button"
                  onClick={() => handleRemoveTemplate(template)}
                  aria-label={`Delete plan template ${template.name}`}
                  title={`Delete ${template.name}`}
                  className="mr-1 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <p className="mb-2 rounded border border-dashed border-border px-3 py-5 text-center text-xs leading-5 text-muted-foreground">No plan templates yet. Save an option to reuse it later.</p>
        )}

        {savingTemplate ? (
          <div className="space-y-2 rounded-md border border-border bg-muted/15 p-2.5">
            <label className="grid grid-cols-[6.5rem_minmax(0,1fr)] items-center gap-2 text-[11px] text-muted-foreground">
              Settings from
              <select
                value={sourceKey}
                aria-label="Plan settings to save"
                onChange={(event) => setDesignSourceKey(event.target.value)}
                className="h-8 min-w-0 rounded border border-border bg-background px-2 text-xs text-foreground"
              >
                {entries.map((entry) => <option key={entry.key} value={entry.key}>{entry.name}</option>)}
              </select>
            </label>
            <label className="grid grid-cols-[6.5rem_minmax(0,1fr)] items-center gap-2 text-[11px] text-muted-foreground">
              Design name
              <input
                autoFocus
                name="saved-design-name"
                autoComplete="off"
                value={templateNameDraft}
                maxLength={40}
                placeholder="Example: Standard safe harbor"
                aria-label={`Name plan template from ${sourceName}`}
                onChange={(event) => setTemplateNameDraft(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && templateNameDraft.trim()) finishSaveTemplate()
                  if (event.key === 'Escape') setSavingTemplate(false)
                }}
                className="h-8 min-w-0 rounded border border-border bg-background px-2 text-xs text-foreground outline-none placeholder:text-muted-foreground focus:ring-1 focus:ring-ring"
              />
            </label>
            <div className="flex justify-end gap-1.5 pt-0.5">
              <button
                type="button"
                onClick={() => setSavingTemplate(false)}
                className="h-7 rounded px-2 text-[11px] text-muted-foreground hover:bg-muted hover:text-foreground"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={!templateNameDraft.trim()}
                onClick={finishSaveTemplate}
                className="h-7 rounded bg-foreground px-2.5 text-[11px] font-medium text-background disabled:cursor-not-allowed disabled:opacity-40"
              >
                Save plan
              </button>
            </div>
          </div>
        ) : null}
      </section> : null}
    </div>
  )
}
